/* eslint-disable react/no-unused-state */
/* eslint-disable no-param-reassign */
import React, { PureComponent } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import sumBy from 'lodash/sumBy';
import AddToCartButton from './components/AddToCartButton';
import ExceededMessage from '../components/ExceededMessage';
import LoadingButton from './components/LoadingButton';
import LocationSelect from './components/selects/LocationSelect';
import Modal from '../components/Modal';
import ResourceSelect from './components/selects/ResourceSelect';
import Submit from './components/Submit';
import WaitlistConnected from '../components/Common/Waitlist';
import { SHORT_DATE_FORMAT } from '../constants';
import { parseCartItems } from '../utilities/cartUtility';
import { stringToBoolean } from '../utilities/conversionUtility';
import BrandingString from './components/BrandingString';
import {
  enableLocationDropdown,
  getScheduleConflictMessages,
  detectIOS,
  isDefaultTitle,
  isHiddenVariant, isScheduled, isDifferentTimezones,
} from '../utilities/helperUtility';
import QuantityInput from './components/QuantityInput';
import Translation from '../utilities/localizationUtility';
import { parseResponseErrors } from '../utilities/parser';
import dayjs from '../../dayjs/dayjs_timezone';
import {
  StyledNextButton,
  StyledExceededAlert,
  StyledVariantSelect,
  StyledWaitlistButton,
  StyledModalContentDiv,
} from './ProductWidgetPage.style';

export default class ProductWidgetPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false,
      isWaitlistModalOpen: false,
    };

    this.checkValidLocation = this.checkValidLocation.bind(this);
    this.checkValidResources = this.checkValidResources.bind(this);
    this.getAvailability = this.getAvailability.bind(this);
    this.getButtonDetails = this.getButtonDetails.bind(this);
    this.getEnableLocationDropdown = this.getEnableLocationDropdown.bind(this);
    this.getEnableResourceDropdown = this.getEnableResourceDropdown.bind(this);
    this.getValidMessage = this.getValidMessage.bind(this);
    this.handleDayMouseLeave = this.handleDayMouseLeave.bind(this);
    this.handleGetCloseModalFn = this.handleGetCloseModalFn.bind(this);
    this.handleGetOpenFnWaitlist = this.handleGetOpenFnWaitlist.bind(this);
    this.handleGetOpenModalFn = this.handleGetOpenModalFn.bind(this);
    this.handleGetValidationFn = this.handleGetValidationFn.bind(this);
    this.modalWrapper = React.createRef();
    this.onAddItems = this.onAddItems.bind(this);
    this.onGetCartItems = this.onGetCartItems.bind(this);
    this.onReservationCreate = this.onReservationCreate.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.openConfirmationModal = this.openConfirmationModal.bind(this);
    this.renderSubmitButton = this.renderSubmitButton.bind(this);
    this.setAlertRef = this.setAlertRef.bind(this);
    this.setDisabledDay = this.setDisabledDay.bind(this);
    this.setTimeslotLoading = this.setTimeslotLoading.bind(this);
    this.setValidateLocation = this.setValidateLocation.bind(this);
    this.setValidResource = this.setValidResource.bind(this);
    this.setWrapperHeight = this.setWrapperHeight.bind(this);
    this.showAddToWaitlistMessage = this.showAddToWaitlistMessage.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
  }

  componentDidMount() {
    const { onTrigger } = this.props;

    if (onTrigger) {
      onTrigger('widgetDidMount', this.getButtonDetails());
    }
  }

  checkValidLocation(message = []) {
    const t = new Translation(this.props);
    let errors;
    const locationDropdown = this.getEnableLocationDropdown();

    if (locationDropdown) {
      errors = this.validateLocation ? this.validateLocation() : [];

      if (errors.length > 0) {
        message.push(t.key('missingLocationMessage'));
      }
    }
  }

  checkValidResources(message = []) {
    const t = new Translation(this.props);
    let errors;
    const resourceDropdown = this.getEnableResourceDropdown();

    if (resourceDropdown) {
      errors = this.validateResource();

      if (errors.length > 0) {
        message.push(t.key('missingResourceMessage') || 'Please select a resource');
      }
    }
  }

  // is used in ancestors
  getAvailability(timeslot) {
    const { maxAvailability, selectedVariant, capacityType } = this.props;
    const variantAvailability = timeslot.availability.find((v) => v.external_id === selectedVariant);
    const isAvailable = (variantAvailability ? variantAvailability.status : 0) === 1;

    if (!isAvailable) {
      return 0;
    }

    switch (capacityType) {
      case 1:
        return variantAvailability.available;
      case 2:
        return timeslot.resource_availability;
      default:
        return maxAvailability - sumBy(timeslot.availability, 'bookings');
    }
  }

  // is overriden in the AppointmentPage
  getButtonDetails() {
    const { setting: { mode } } = this.props;

    if (mode !== 'popup') {
      return null;
    }

    const t = new Translation(this.props);

    return {
      label: t.key('modalTitle'),
    };
  }

  getConditionForDisabled(_dateValue, strValue) {
    const { availableDates, availableFinishDates } = this.state;
    return !(availableDates.includes(strValue) || (this.isRoom() && availableFinishDates.includes(strValue)));
  }

  isRoom() {
    const { product: { profile } } = this.props;
    return profile === 'room';
  }

  // is used in ancestors
  // is overriden in the ActivityPage
  // eslint-disable-next-line react/no-unused-class-component-methods
  getDuration(variantSelected) {
    const { durations, order, variants } = this.props;
    if (!variantSelected) {
      variantSelected = order?.variantSelected;
    }

    if (durations.type !== 1) {
      return durations.duration;
    }

    const selectedVariant = variants.find((v) => v.external_id === variantSelected);

    if (!selectedVariant) {
      return 0;
    }

    let optionDuration = this.getOptionDuration(selectedVariant);

    if (isDefaultTitle(selectedVariant.title)) {
      [optionDuration] = durations.option_durations;
    }

    return optionDuration ? optionDuration.duration : 0;
  }

  getEnableLocationDropdown() {
    const { setting, locations } = this.props;
    return enableLocationDropdown(locations, setting.show_location_chooser);
  }

  getEnableResourceDropdown() {
    const { setting, resources } = this.props;
    const showResourceChooser = stringToBoolean(setting.show_resource_chooser);
    return showResourceChooser && resources.length > 1;
  }

  // is overridden in AppointmentPage, CoursePage, RentalPage
  getNextButtonLabel(canBook) {
    const { selectedTimeslot } = this.props;
    const t = new Translation(this.props);
    let label = canBook ? t.key('nextButton') : t.key('noAvailability');

    if (!selectedTimeslot) {
      label = t.key('makeSelection');
    }

    return label;
  }

  getOptionDuration(selectedVariant) {
    const { durations, options } = this.props;

    const optionIdx = findIndex(options, (o) => o.name === durations.option);
    return durations.option_durations.find((d) => d.value === selectedVariant.options[optionIdx]);
  }

  getValidMessage() {
    const messages = [];

    this.checkValidLocation(messages);
    this.checkValidResources(messages);

    return messages;
  }

  // overridden in ActivityPage, AppointmentPage, CoursePage, RentalPage
  getSubmitButtonLabel() {
    const { selectedTimeslot, bookingToken } = this.props;
    const t = new Translation(this.props);
    let submitLabel;

    if (!selectedTimeslot) {
      submitLabel = t.key('makeSelection');
    } else if (bookingToken) {
      submitLabel = t.key('reschedule') || 'Reschedule';
    }

    return submitLabel;
  }

  // eslint-disable-next-line class-methods-use-this
  handleAddItems() {
    // to be overriden in ancestors
  }

  handleDayMouseLeave() {
    this.setState({
      hoverStart: null,
      hoverEnteredTo: null,
    });
  }

  handleGetCloseModalFn(closeFn) {
    this.closeModal = closeFn;
  }

  handleGetOpenFnWaitlist(openFn) {
    this.openWaitlistModal = openFn;
  }

  handleGetOpenModalFn(openFn) {
    this.openModal = openFn;
  }

  handleAfterWaitlistModalOpen = () => this.setState({ isWaitlistModalOpen: true });

  handleAfterWaitlistModalClose = () => this.setState({ isWaitlistModalOpen: false });

  // is used in ancestors
  // overridden in AppointmentPage
  handleGetValidationFn(validationFn) {
    this.validateAdditionalDetails = validationFn;
  }

  isEnabledBookingFields() {
    const { setting, bookingFields } = this.props;
    const showBookingFields = stringToBoolean(setting.show_booking_fields);
    return showBookingFields && bookingFields.length > 0;
  }

  onAddItems({
    reservation,
    checkAllDay,
    firstSliceParam = 0,
    secondSliceParam = 1,
    type,
    addItemsActionParam,
    callback,
  }) {
    const {
      additionalDetails,
      allDay,
      bookingFields,
      bookingToken,
      cartActions,
    } = this.props;
    const reservationCopy = cloneDeep(reservation);

    if (reservationCopy) {
      reservationCopy.items = reservationCopy.items.slice(firstSliceParam, secondSliceParam);
    }

    if (!reservationCopy || reservationCopy.items.some((x) => x.status !== 'ok')) {
      if (callback) {
        callback();
      }
    } else if (bookingToken) {
      this.onBookingReschedule(reservationCopy.items[0]);
    } else {
      const visibleResource = this.getEnableResourceDropdown();
      const fieldsSetting = { additionalDetails, bookingFields };

      if (checkAllDay && !allDay) {
        type.splice(1, 0, 'Time');
      }

      type.push(visibleResource ? 'Resource' : '_resource');
      cartActions.addItems(reservationCopy, type, fieldsSetting, addItemsActionParam);
    }
  }

  buildItems() {
    const { orderVariants } = this.props;
    return orderVariants;
  }

  onSuccessReservationCreate(ele, response) {
    const { reservation } = response.data;
    const t = new Translation(this.props);
    this.onAddItems(reservation, () => {
      const { fullMessage } = getScheduleConflictMessages(reservation, t);
      ele.disabled = false;
      this.setState({ messages: [fullMessage] });
    });
  }

  onFailedReservationCreate(ele, responsed) {
    ele.disabled = false;
    const t = new Translation(this.props);
    const { response = { data: {} } } = responsed;
    const {
      error_items: errorItems,
      errors,
      reservation,
      variants,
    } = response.data;

    this.onAddItems(reservation, () => {
      if (!!errorItems || !!errors) {
        const newResponse = { data: { items: errorItems, errors, variants } };
        this.setState({ messages: parseResponseErrors(this.buildItems(), newResponse, t) });
      } else {
        this.setState({ messages: ['Something went wrong. Please contact our support team.'] });
        throw responsed;
      }
    });
  }

  onReservationCreate(ele, cartItems) {
    this.reservationCreateFn(cartItems)
      .then((response) => { this.onSuccessReservationCreate(ele, response); })
      .catch((responsed) => { this.onFailedReservationCreate(ele, responsed); });
  }

  onBookingReschedule(item) {
    const { orderActions } = this.props;
    orderActions
      .rescheduleBookingByToken(this.buildReschedulePayload(item))
      .then(() => {
        this.closeModal();
        this.setState({ bookingUpdated: true });
      })
      .catch((responsed) => {
        const { response } = responsed;
        if (response && response.data) {
          this.setState({ messages: [response.data.message] });
        } else {
          this.setState({ messages: ['Something went wrong. Please contact our support team.'] });
        }
      });
  }

  onGetCartItems({
    element,
    itemsResponse,
    action,
    isShowAlert = false,
  }) {
    const { onTrigger, reservationEnabled } = this.props;

    if (onTrigger && reservationEnabled) {
      onTrigger(
        'getCartItems',
        null,
        (items) => this.onReservationCreate({
          element,
          cartItems: parseCartItems(items),
          itemsResponse,
          action,
          isShowAlert,
        }),
      );
    } else {
      this.onReservationCreate({
        element,
        cartItems: [],
        itemsResponse,
        action,
        isShowAlert,
      });
    }
  }

  onReservationCreate({
    element,
    cartItems,
    itemsResponse,
    action,
    isShowAlert,
  }) {
    const t = new Translation(this.props);
    element.disabled = true;

    action(cartItems)
      .then((response) => {
        const { reservation } = response.data;

        this.handleAddItems(reservation, () => {
          const { fullMessage } = getScheduleConflictMessages(reservation, t);
          element.disabled = false;

          if (isShowAlert) {
            this.alert.show('red', [fullMessage]);
          } else {
            this.setState({ messages: [fullMessage] });
          }
        });
      })
      .catch((responsed) => {
        element.disabled = false;
        const { response = { data: {} } } = responsed;
        const {
          reservation,
          error_items: errorItems,
          errors,
          variants,
        } = response.data;

        this.handleAddItems(reservation, () => {
          if (!!errorItems || !!errors) {
            const newResponse = { data: { items: errorItems, errors, variants } };
            this.setState({ messages: parseResponseErrors(itemsResponse, newResponse, t) });
          } else {
            this.setState({ messages: ['Something went wrong. Please contact our support team.'] });
            throw responsed;
          }
        });
      });
  }

  // overridden in the AppointmentPage
  setStateOnGetCartItems({ element, messages, itemsResponse, action, isShowAlert }) {
    if (!messages.length) {
      this.setState({ messages: [] }, () => this.onGetCartItems({
        element: element.target,
        itemsResponse,
        action,
        isShowAlert,
      }));
    }
  }

  // is used in ancestors
  onSubmit({
    element,
    messages,
    itemsResponse,
    action,
    bookingFields,
    isEnableBookingCreation,
    isShowAlert = false,
  }) {
    const { setting: { show_booking_fields } } = this.props;
    const showBookingFields = stringToBoolean(show_booking_fields);
    const t = new Translation(this.props);
    const condition = bookingFields ? showBookingFields && bookingFields.length : showBookingFields;

    if (condition && this.validateAdditionalDetails) {
      const errors = this.validateAdditionalDetails();

      if (errors.length > 0) {
        messages.push(t.key('requiredFieldsMessage'));
      }
    }

    if (isEnableBookingCreation) {
      if (this.validateCustomerDetails().length > 0) {
        messages.push(t.key('requiredFieldsMessage'));
      }
    }

    this.setStateOnGetCartItems({ element, messages, itemsResponse, action, isShowAlert });
  }

  // eslint-disable-next-line class-methods-use-this
  checkVariants() {
    // to be overriden in ancestors
  }

  openConfirmationModal() {
    const messages = this.getValidMessage();

    if (!messages.length) {
      this.checkVariants();
      this.setState({ messages: [] });
      this.openModal();
    }
  }

  // is used in ancestors
  setAlertRef(ref) {
    this.alert = ref;
  }

  setDisabledDay(value) {
    const day = dayjs(value).format(SHORT_DATE_FORMAT);

    if (this.getConditionForDisabled(value, day)) {
      return new Date(day);
    }

    return null;
  }

  setTimeslotLoading(value) {
    if (detectIOS()) {
      if (value) {
        this.setState({ timeslotLoading: value });
      } else {
        setTimeout(() => {
          this.setState({ timeslotLoading: value });
        }, 1000);
      }
    }
  }

  setValidateLocation(func) {
    const { bookingToken } = this.props;
    if (bookingToken) { // do not validate if rescheduling
      return;
    }
    this.validateLocation = func;
  }

  setValidResource(func) {
    const { bookingToken } = this.props;
    if (bookingToken) { // do not validate if rescheduling
      return;
    }
    this.validateResource = func;
  }

  // is overriden in the CoursePage
  // eslint-disable-next-line class-methods-use-this
  buildReschedulePayload(item) {
    return { booking: { start: item.start, finish: item.finish } };
  }

  setWrapperHeight() {
    const { isModalOpen, modalHeight } = this.state;
    let height;

    if (!isModalOpen) {
      height = 100;
    }

    if (!height) {
      const wrapper = this.modalWrapper.current;
      height = wrapper ? wrapper.clientHeight : modalHeight;
    }

    if (height !== modalHeight) {
      this.setState({ modalHeight: height });
    }
  }

  showAddToWaitlistMessage() {
    const t = new Translation(this.props);
    this.alert.show('success', [t.key('addToWaitlistMessage')]);
  }

  // is used in ancestors
  // overridden in the RentalPage
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderButton(canBook, conditionForLoadingButton, conditionForRender) {
    const { order, variants, selectedVariant } = this.props;
    const variantSelected = selectedVariant || order?.variantSelected;
    const isNextButton = conditionForRender || this.isEnabledBookingFields();

    if (conditionForLoadingButton) {
      return <LoadingButton />;
    }

    if (isHiddenVariant(variants, variantSelected)) {
      return <AddToCartButton />;
    }

    return isNextButton ? this.renderNextButton(canBook) : this.renderSubmitButton(canBook);
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderExceededAlert(needTextLeft) {
    const { exceededBookingsLimit, exceededTrialPlan, messages } = this.props;
    const exceeded = exceededBookingsLimit || exceededTrialPlan;

    if (!exceeded) {
      return null;
    }

    return (
      <StyledExceededAlert
        type="exceeded-trial-plan"
        messages={[<ExceededMessage message={messages.message_booking_limit_reached} />]}
        unclosed
        needTextLeft={needTextLeft}
        disableIcon
      />
    );
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderLocationDropdown(isDisabled) {
    const { locations, product: { profile } } = this.props;
    const t = new Translation(this.props);
    const enableLD = this.getEnableLocationDropdown();
    let blankOption = null;

    if (!enableLD) {
      return null;
    }

    const { style: { body } } = this.props;

    if (!isScheduled(profile) || !isDifferentTimezones(locations)) {
      blankOption = {
        label: t.key('locationDropdownPrompt') || 'Choose any',
        value: '',
      };
    }

    return (
      <LocationSelect
        className="mt10"
        getValidationFn={this.setValidateLocation}
        errorMessage={t.key('missingLocationMessage')}
        label={t.key('locationPrompt')}
        style={body}
        blankOption={blankOption}
        isDisableClearable
        isDisabled={isDisabled}
      />
    );
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderModal(child, showBrand) {
    const { style } = this.props;
    const boxShadowUnsetClass = style.boxShadow.boxShadow === 'unset' ? 'box-shadow-unset' : '';

    return (
      <Modal
        fullPage
        closeWord={' '}
        getOpenFn={this.handleGetOpenModalFn}
        getCloseFn={this.handleGetCloseModalFn}
        onAfterOpen={() => this.setState({ isModalOpen: true }, this.setWrapperHeight)}
        onAfterClose={() => this.setState({ isModalOpen: false }, this.setWrapperHeight)}
        portalClassName={`confirmation-page ${boxShadowUnsetClass}`}
        customStyle={{
          overlay: {
            margin: `${style.container?.margin}px`,
            borderRadius: `${style.container?.widgetContainerBorderRadius}px`,
          },
          borderRadius: `${style.container?.widgetContainerBorderRadius}px`,
        }}
      >
        <StyledModalContentDiv className="relative" style={style} ref={this.modalWrapper}>
          {child}
          {showBrand && <BrandingString />}
        </StyledModalContentDiv>
      </Modal>
    );
  }

  renderNextButton(canBook) {
    const { style } = this.props;
    const backgroundColor = canBook ? style.button.backgroundColor : style.inactive.backgroundColor;
    const nextText = this.getNextButtonLabel(canBook);

    return (
      <StyledNextButton
        styled={style.button}
        disabled={!canBook}
        onClick={this.openConfirmationModal}
        submit
        bc={backgroundColor}
        fontSize={style.body.fontSize}
      >
        {nextText}
      </StyledNextButton>
    );
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderResourceDropdown(isDisabled, onlyMissingMessage) {
    const enableRD = this.getEnableResourceDropdown();

    if (!enableRD) {
      return null;
    }

    const { style: { body } } = this.props;
    const t = new Translation(this.props);
    let message = t.key('missingResourceMessage') || 'Please select a resource';

    if (onlyMissingMessage) {
      message = t.key('missingResourceMessage');
    }

    return (
      <ResourceSelect
        className="mt10"
        style={body}
        label={t.key('resourcePrompt')}
        getValidationFn={this.setValidResource}
        errorMessage={message}
        isDisabled={isDisabled}
      />
    );
  }

  // is used in ancestors
  // overridden in ActivityPage
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderSubmitButton(canBook) {
    const { messages } = this.state;
    const submitLabel = this.getSubmitButtonLabel();

    return (
      <Submit
        label={submitLabel}
        messages={messages}
        onSubmit={this.handleSubmit}
        canBook={canBook}
      />
    );
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderQuantityInput(max, label = 'quantity', min = 1) {
    const { setting } = this.props;
    const t = new Translation(this.props);
    const showQuantityChooser = stringToBoolean(setting.show_quantity_chooser);

    if (showQuantityChooser) {
      return (
        <QuantityInput
          min={min}
          maxQuantity={max}
          label={t.key(label)}
        />
      );
    }

    return null;
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods, class-methods-use-this
  renderVariantSelect(isDisabled) {
    return (
      <StyledVariantSelect isDisabled={isDisabled} />
    );
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderWaitlist(selectedTimeslot, options = {}) {
    const { finish, needActivity } = options;
    if (this.showWaitlist()) {
      return (
        <WaitlistConnected
          alert={this.alert}
          isActivity={needActivity}
          getOpenFn={this.handleGetOpenFnWaitlist}
          onAfterOpen={this.handleAfterWaitlistModalOpen}
          onAfterClose={this.handleAfterWaitlistModalClose}
          selected={selectedTimeslot}
          finish={finish}
          onAddItem={this.showAddToWaitlistMessage}
          displayPhoneNumberField={this.displayPhoneNumberOnWaitlist()}
          displayLocationField={this.displayLocationOnWaitlist()}
        />
      );
    }

    return null;
  }

  // is used in ancestors
  // eslint-disable-next-line react/no-unused-class-component-methods
  renderWaitlistButton() {
    const { style } = this.props;
    const t = new Translation(this.props);

    return (
      <StyledWaitlistButton
        styled={style.button}
        submit
        onClick={this.openWaitlistModal}
        fs={style.body.fontSize}
      >
        {t.key('addToWaitlist')}
      </StyledWaitlistButton>
    );
  }

  showWaitlist() {
    const { ability, setting } = this.props;
    return stringToBoolean(setting.allow_waitlist) && ability.isStandard;
  }

  displayPhoneNumberOnWaitlist() {
    const { setting } = this.props;

    // Defaults to true if the corresponding key in the setting is not defined yet
    // This is to ensure backward compatibility with the existing widgets
    return stringToBoolean(setting.display_phone_number_on_waitlist ?? 'true');
  }

  displayLocationOnWaitlist() {
    const { setting } = this.props;

    // Defaults to true if the corresponding key in the setting is not defined yet
    // This is to ensure backward compatibility with the existing widgets
    return stringToBoolean(setting.display_location_on_waitlist ?? 'true');
  }

  updateDimensions() {
    this.setState({
      width: window.innerWidth,
    });
  }
}
